import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Input from '../../components/Input';
import { useFormik } from 'formik';
import useApi from '../../hooks/use-api';
import { AppRoutes } from '../../utils/props';
import { OutlinedCheck, OutlinedError } from '../../icons';

export default function ChangePasswordPage() {
  const { t } = useTranslation('ns');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [submitted, setSubmitted] = useState(false);

  const changePassword = useApi({
    path: `/change_password/${token}`,
    defaultSuccessMessage: t('passwordUpdated', { defaultValue: 'Your password has been updated. Redirecting to login...' }),
    callback: () => {
      setSubmitted(true);
      setTimeout(() => navigate(AppRoutes.Login), 2000);
    },
  });

  const form = useFormik({
    initialValues: { password: '', confirmPassword: '' },
    validate: values => {
      const errors: { password?: string; confirmPassword?: string } = {};
      if (!values.password) {
        errors.password = t('passwordRequired', { defaultValue: 'Password is required' });
      }
      if (values.confirmPassword !== values.password) {
        errors.confirmPassword = t('passwordsMustMatch', { defaultValue: 'Passwords must match' });
      }
      return errors;
    },
    onSubmit: async (values) => {
      await changePassword.post({ new_password: values.password });
    },
  });

  const { errors, touched, getFieldProps, handleSubmit } = form;

  return (
    <div
      className="relative flex min-h-screen w-full flex-col items-center justify-center bg-cover bg-center"
      style={{
        background: `linear-gradient(180deg, #086CE1 0%, rgba(8, 108, 225, 0) 98%), linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000 98%), url('/images/account-background.jpg') lightgray -2.651px -381.993px/100.239% 142.326% no-repeat`,
      }}
    >
      <div className="relative z-10 flex w-full flex-col items-center">
        <h1 className="mx-auto mb-12 text-white max-w-lg text-center text-3xl font-extrabold text-primary drop-shadow-lg md:text-4xl">
          {t('changePasswordTitle', { defaultValue: 'Change your password' })}
        </h1>
        <div className="w-full max-w-md px-4">
          {!token && (
            <div
              role="alert"
              className="mb-4 flex items-start gap-2 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600"
            >
              <OutlinedError
                width={20}
                height={20}
                hardcodedColor="#DC2626"
                className="mt-0.5 shrink-0"
              />
              <span>{t('invalidResetLink', { defaultValue: 'This password reset link is invalid or has expired. Please request a new one.' })}</span>
            </div>
          )}
          {changePassword.errors && (
            <div
              role="alert"
              className="mb-4 flex items-start gap-2 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600"
            >
              <OutlinedError
                width={20}
                height={20}
                hardcodedColor="#DC2626"
                className="mt-0.5 shrink-0"
              />
              <span>{changePassword.errors}</span>
            </div>
          )}
          {submitted && (
            <div
              role="status"
              className="mb-4 flex items-start gap-2 rounded-lg border border-green-100 bg-green-50 px-4 py-3 text-sm text-green-700"
            >
              <OutlinedCheck
                width={20}
                height={20}
                hardcodedColor="#15803D"
                className="mt-0.5 shrink-0"
              />
              <span>{t('passwordUpdated', { defaultValue: 'Your password has been updated. Redirecting to login...' })}</span>
            </div>
          )}
          <form className="flex flex-col items-center px-8 pb-8 pt-10" onSubmit={handleSubmit}>
            <div className="mb-2 w-full">
              <Input
                {...getFieldProps('password')}
                title={t('newPassword')}
                type="password"
                error={touched.password ? (errors.password ?? '') : ''}
                labelClassName="text-white"
                disabled={!token || submitted}
              />
            </div>
            <div className="mb-2 w-full">
              <Input
                {...getFieldProps('confirmPassword')}
                title={t('confirmNewPassword')}
                type="password"
                error={touched.confirmPassword ? (errors.confirmPassword ?? '') : ''}
                labelClassName="text-white"
                disabled={!token || submitted}
              />
            </div>
            <div className="mb-4 flex w-full justify-end">
              <Link
                className="bg-transparent text-xs font-medium text-blue-100 transition hover:underline"
                to={AppRoutes.Login}
              >
                {t('backToLogin', { defaultValue: 'Back to login' })}
              </Link>
            </div>
            <button
              className="mb-6 w-full rounded bg-blue-600 py-3 text-lg font-bold text-white shadow-md transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              type="submit"
              disabled={!token || submitted || changePassword.loading}
            >
              {t('updatePassword', { defaultValue: 'Update Password' })}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
